// One client: how to reach them, what they owe and have paid, and every
// invoice they've been sent.
import { Link, useParams } from 'react-router-dom';
import { useAppData } from '@/app/AppData';
import { usePageTitle } from '@/app/usePageTitle';
import { clientInitials, clientStats, displayName, isGoodPayer } from '@/core/client';
import { today } from '@/core/dates';
import { track } from '@/data/analytics';
import { t } from '@/l10n/strings';
import { InvoiceTable } from '@/pages/Invoices';
import { Avatar, ButtonLink, EmptyState, PageLoading } from '@/ui/controls';
import { Icon } from '@/ui/Icon';
import { emailDraftHref, phoneHref, whatsappHref } from '@/utils/contactLinks';

export function ClientDetailPage() {
  const { id } = useParams();
  const { clients, invoices } = useAppData();
  const client = clients?.find((c) => c.id === id);
  usePageTitle(client ? displayName(client) : t('webTitleClient'));
  if (!clients || !invoices) return <PageLoading />;
  if (!client) {
    return (
      <div className="page">
        <EmptyState
          badge="users"
          title={t('clientDetailThisClientIsGone')}
          body={t('clientDetailTheyWereDeletedMaybeOn')}
          action={
            <ButtonLink to="/clients" variant="action">
              {t('navClients')}
            </ButtonLink>
          }
        />
      </div>
    );
  }
  const stats = clientStats(client.id, invoices, today());
  const theirs = invoices.filter((i) => i.clientId === client.id);
  const name = displayName(client);
  const shortName = name.split(/\s+/)[0];
  const phone = phoneHref(client.phone);
  return (
    <div className="page">
      <header className="page-head">
        <div className="page-head__title">
          <Link to="/clients" className="back-link">
            <Icon name="back" size={16} />
            {t('navClients')}
          </Link>
          <div className="row" style={{ gap: 16 }}>
            <Avatar initials={clientInitials(client)} seed={name} size="lg" />
            <div className="stack-1">
              <h1 className="display">{name}</h1>
              {client.company.trim() && client.name.trim() && <p className="body muted">{client.name}</p>}
            </div>
            {isGoodPayer(stats) && (
              <span className="chip chip--paid">
                <i />
                {t('clientsGoodPayer')}
              </span>
            )}
          </div>
        </div>
        <div className="page-head__actions">
          <ButtonLink to={`/clients/${client.id}/edit`} icon="pen">
            {t('clientDetailEditClient')}
          </ButtonLink>
          <ButtonLink to={`/invoices/new?clientId=${client.id}`} variant="action" icon="plus">
            {t('clientDetailNewInvoiceFor', { shortName })}
          </ButtonLink>
        </div>
      </header>

      <div className="client-grid">
        <section className="stack-4">
          <div className="grid-2">
            <div className="kpi">
              <span className="caption">{t('clientDetailOwesYou')}</span>
              <b className={`amount-lg${stats.overdue.isPositive ? ' danger-text' : ''}`}>{stats.owed.format()}</b>
              {stats.overdue.isPositive && <span className="caption danger-text">{t('clientsOverdue', { owed: stats.overdue.format() })}</span>}
            </div>
            <div className="kpi">
              <span className="caption">{t('clientDetailPaidSoFar')}</span>
              <b className="amount-lg">{stats.paid.format()}</b>
              {stats.averageDaysToPay !== null && (
                <span className="caption">
                  {t('clientsPaysIn')} {t('clientsPaysInDays', { days: stats.averageDaysToPay })}
                </span>
              )}
            </div>
          </div>
          <h2 className="title-serif">{t('clientDetailInvoices')}</h2>
          {theirs.length ? (
            <InvoiceTable invoices={theirs} hideClient />
          ) : (
            <div className="card">
              <EmptyState
                title={t('clientDetailNoInvoicesFor', { shortName })}
                action={
                  <ButtonLink to={`/invoices/new?clientId=${client.id}`} variant="action" icon="plus">
                    {t('clientDetailNewInvoiceFor', { shortName })}
                  </ButtonLink>
                }
              />
            </div>
          )}
        </section>
        <aside className="card stack-3">
          <h2 className="headline">{t('clientsContact')}</h2>
          {client.email && (
            <a className="row" href={emailDraftHref(client.email, '', '')} onClick={() => track('client_contacted', { channel: 'email' })}>
              <Icon name="mail" size={18} />
              {client.email}
            </a>
          )}
          {phone && (
            <a className="row" href={phone} onClick={() => track('client_contacted', { channel: 'call' })}>
              <Icon name="phone" size={18} />
              {client.phone}
            </a>
          )}
          {client.phone.trim() && (
            <a className="row" href={whatsappHref(client.phone)} target="_blank" rel="noreferrer" onClick={() => track('client_contacted', { channel: 'whatsapp' })}>
              <Icon name="wa" size={18} />
              {t('clientDetailWhatsApp')}
            </a>
          )}
          {client.address.trim() && <p className="body ink2" style={{ whiteSpace: 'pre-line' }}>{client.address}</p>}
          {client.notes.trim() && (
            <>
              <hr className="divider" />
              <span className="overline">{t('clientDetailNotesOnlyYouSeeThese')}</span>
              <p className="body" style={{ whiteSpace: 'pre-line' }}>
                {client.notes}
              </p>
            </>
          )}
        </aside>
      </div>
    </div>
  );
}
